import {
  autoPlacement,
  autoUpdate,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  arrow,
  UseFloatingOptions,
  ExtendedRefs,
  FloatingContext,
  UseRoleProps,
} from '@floating-ui/react'
import { useRef, useState } from 'react'

export interface IPopover<T extends HTMLElement> {
  arrowRef?: React.MutableRefObject<null>
  isOpen: boolean
  toggle: () => void
  target: (node: T | null) => void
  refs: ExtendedRefs<T>
  targetProps: React.HTMLAttributes<T>
  getFloatingProps: () => React.HTMLAttributes<HTMLDivElement>
  floatingStyles: React.CSSProperties
  context: FloatingContext<any>
}

export interface IPopperProps
  extends Omit<Partial<UseFloatingOptions>, 'placement' | 'onOpenChange'> {
  roleType?: UseRoleProps['role']
  placement?: UseFloatingOptions['placement'] | 'auto'
  handleToggle?: (shown: boolean) => any
  onOpenChange?: React.Dispatch<React.SetStateAction<boolean>>
}
export const usePopper = <T extends HTMLElement>({
  roleType = 'menu',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  handleToggle,
  placement,
  ...config
}: IPopperProps = {}): IPopover<T> => {
  const arrowRef = useRef(null)
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = controlledOpen ?? uncontrolledOpen
  const setIsOpen = setControlledOpen ?? setUncontrolledOpen
  const { refs, floatingStyles, context } = useFloating<T>({
    ...config,
    open: isOpen,
    placement: placement === 'auto' ? undefined : placement,
    onOpenChange: setIsOpen,
    middleware: [
      !placement || placement === 'auto' ? autoPlacement() : undefined,
      arrow({
        element: arrowRef,
      }),
      ...(config.middleware || []),
    ].filter(Boolean),
    whileElementsMounted: autoUpdate,
  })

  const toggle = () => {
    setIsOpen((open) => !open)
    handleToggle?.(!isOpen)
  }

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: roleType })
  const interactions = [click, dismiss, role]

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions)
  return {
    target: refs.setReference,
    refs,
    targetProps: getReferenceProps(),
    getFloatingProps,
    floatingStyles,
    arrowRef,
    isOpen,
    context,
    toggle,
  }
}
